import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import AdminTopBar from "./AdminTopBar";
import {
    ClockIcon,
    RefreshIcon,
    ClipboardCheckIcon,
  } from "@heroicons/react/outline";
import Draggable from "react-draggable";

const FileViewer = () => {
  const location = useLocation();
  const submission = location.state?.submission;

  const documentUrl = submission.applicationFile;
  const googleDocsViewerUrl = `https://docs.google.com/gview?url=${encodeURIComponent(documentUrl)}&embedded=true`;

  // State to track the iframe loading status
  const [loading, setLoading] = useState(true);
  const [iframeKey, setIframeKey] = useState(0); // For refreshing the iframe

  // Function to refresh the iframe
  const handleRefresh = () => {
    setIframeKey((prevKey) => prevKey + 1); // Change the key to re-render iframe
  };

  // Function to toggle loading state when iframe is done loading
  const handleIframeLoad = () => {
    setLoading(false); // Hide loading spinner when iframe finishes loading
  };

  // State to handle score form modal
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isTimerOpen, setIsTimerOpen] = useState(false); // Timer pop-up state
  const [minutes, setMinutes] = useState(10); // Default timer to 10 minutes
  const [timeLeft, setTimeLeft] = useState(600); // Default countdown in seconds (10 mins)
  const [isCounting, setIsCounting] = useState(false); // Track if the timer is running

  // Function to toggle form modal
  const toggleForm = () => {
    setIsFormOpen(!isFormOpen);
  };

  // Countdown logic
  useEffect(() => {
    let timer;
    if (isCounting && timeLeft > 0) {
      timer = setTimeout(() => {
        setTimeLeft(timeLeft - 1);
      }, 1000);
    } else if (timeLeft === 0) {
      setIsCounting(false); // Stop the timer when it reaches 0
    }
    return () => clearTimeout(timer);
  }, [isCounting, timeLeft]);

  // Function to handle setting the timer
  const handleSetTimer = (e) => {
    e.preventDefault();
    setTimeLeft(minutes * 60);
    setIsTimerOpen(false);
    setIsCounting(true);
  };

  return (
    <>

      <div className="flex flex-col h-screen">
        {/* Header section with Score, Refresh, and Timer buttons */}
        <div className="p-6 bg-gray-100 border-b flex justify-between items-center">
          <h2 className="text-xl font-bold">File Preview</h2>

          <div className="flex space-x-4">
            {/* Timer Button */}
            <button
              className="bg-blue-500 text-white px-4 py-2  hover:bg-blue-600 flex items-center"
              onClick={() => setIsTimerOpen(true)}
            >
              <ClockIcon className="h-5 w-5 mr-2" />
              Timer
            </button>

            {/* Refresh Button */}
            <button
              className="bg-custom-blue-1 text-white px-4 py-2  hover:bg-custom-blue-1 flex items-center"
              onClick={handleRefresh}
            >
              <RefreshIcon className="h-5 w-5 mr-2" />
              Refresh
            </button>

            {/* Score Button */}
            <button
              className="bg-custom-green-1 text-white px-4 py-2  hover:bg-custom-green-1 flex items-center"
              onClick={toggleForm}
            >
              <ClipboardCheckIcon className="h-5 w-5 mr-2" />
              Score
            </button>
          </div>
        </div>

        {/* Timer Display */}
        {isCounting && (
          <div className="p-4 bg-yellow-100 text-yellow-800 border-b flex justify-between items-center">
            <span className="font-bold">
              Time Left: {Math.floor(timeLeft / 60)}:
              {("0" + (timeLeft % 60)).slice(-2)} minutes
            </span>
            <button
              className="bg-red-500 text-white px-4 py-2  hover:bg-red-600"
              onClick={() => setIsCounting(false)}
            >
              Stop Timer
            </button>
          </div>
        )}

        {/* Responsive iframe container with loading spinner */}
        <div className="flex-grow relative">
          {loading && (
            <div className="absolute inset-0 flex justify-center items-center bg-white bg-opacity-50 z-50">
              {/* Spinner */}
              <div className="loader ease-linear rounded-full border-8 border-t-8 border-gray-200 h-16 w-16"></div>
            </div>
          )}
          <iframe
            key={iframeKey} // Use key to trigger iframe reload
            src={googleDocsViewerUrl}
            className="w-full h-full"
            title="Document Viewer"
            style={{ border: "none" }}
            onLoad={handleIframeLoad} // Hide loading spinner on load
          />
        </div>

        {/* Draggable Floating Score Form */}
        {isFormOpen && (
          <Draggable handle=".draggable-header">
            <div className="fixed z-50 bg-white p-6 rounded shadow-lg w-1/3 top-1/4 left-1/4">
              {/* Draggable Header */}
              <div className="draggable-header cursor-move flex justify-between items-center mb-4">
                <h2 className="text-lg font-bold">Enter Scores</h2>
                <button
                  className="text-gray-500 hover:text-red-600"
                  onClick={toggleForm}
                >
                  &#x2715; {/* Close button */}
                </button>
              </div>

              {/* Score Input Form */}
              <form>
                <div className="mb-4">
                  <label className="block text-gray-700">
                    Problem Definition
                  </label>
                  <input
                    type="number"
                    min="0"
                    max="15"
                    className="w-full border border-gray-300 p-2 rounded"
                    placeholder="Score (0-15)"
                  />
                </div>

                <div className="mb-4">
                  <label className="block text-gray-700">Solution</label>
                  <input
                    type="number"
                    min="0"
                    max="15"
                    className="w-full border border-gray-300 p-2 rounded"
                    placeholder="Score (0-15)"
                  />
                </div>

                {/* More fields can be added here */}

                <div className="flex justify-end">
                  <button
                    type="button"
                    className="bg-gray-500 text-white px-4 py-2 rounded mr-2"
                    onClick={toggleForm}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="bg-blue-500 text-white px-4 py-2 rounded"
                  >
                    Submit
                  </button>
                </div>
              </form>
            </div>
          </Draggable>
        )}

        {/* Timer Popup */}
        {isTimerOpen && (
          <div className="fixed inset-0 flex justify-center items-center bg-black bg-opacity-50 z-50">
            <div className="bg-white p-6 rounded shadow-lg w-1/3">
              <h2 className="text-lg font-bold mb-4">Set Timer (Minutes)</h2>
              <form onSubmit={handleSetTimer}>
                <input
                  type="number"
                  min="1"
                  value={minutes}
                  onChange={(e) => setMinutes(e.target.value)}
                  className="w-full border border-gray-300 p-2 rounded mb-4"
                  placeholder="Set timer (in minutes)"
                />
                <div className="flex justify-end">
                  <button
                    type="button"
                    className="bg-gray-500 text-white px-4 py-2 rounded mr-2"
                    onClick={() => setIsTimerOpen(false)}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="bg-blue-500 text-white px-4 py-2 rounded"
                  >
                    Start Timer
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>

      {/* Spinner CSS */}
      <style jsx>{`
        .loader {
          border-top-color: #3498db;
          animation: spin 1s linear infinite;
        }
        @keyframes spin {
          0% {
            transform: rotate(0deg);
          }
          100% {
            transform: rotate(360deg);
          }
        }
      `}</style>
    </>
  );
};

export default FileViewer;
